
import React, { useState, useEffect, useRef } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { ChevronLeft, ChevronRight } from "lucide-react";

export interface TabItem {
  value: string;
  label: React.ReactNode;
  content: React.ReactNode;
  disabled?: boolean;
}

interface ResponsiveTabsProps {
  defaultValue?: string;
  tabs: TabItem[];
  className?: string;
  tabsListClassName?: string;
  contentClassName?: string;
  orientation?: "horizontal" | "vertical";
  onChange?: (value: string) => void;
  onValueChange?: (value: string) => void; // Added for backward compatibility
}

export const ResponsiveTabs: React.FC<ResponsiveTabsProps> = ({
  defaultValue,
  tabs,
  className,
  tabsListClassName,
  contentClassName,
  orientation = "horizontal",
  onChange,
  onValueChange,
}) => {
  const [activeTab, setActiveTab] = useState(defaultValue || tabs[0]?.value);
  const tabsRef = useRef<HTMLDivElement>(null);
  const [showScrollButtons, setShowScrollButtons] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // Check if we need to show scroll buttons (on small screens)
  useEffect(() => {
    const checkScroll = () => {
      if (tabsRef.current) {
        const { scrollWidth, clientWidth, scrollLeft } = tabsRef.current;
        const needsScrolling = scrollWidth > clientWidth;
        
        setShowScrollButtons(needsScrolling);
        setCanScrollLeft(scrollLeft > 0);
        setCanScrollRight(scrollLeft + clientWidth < scrollWidth);
      }
    };

    checkScroll();
    window.addEventListener('resize', checkScroll);
    
    return () => {
      window.removeEventListener('resize', checkScroll);
    };
  }, [tabs.length]);

  // Update scroll indicators when we scroll
  const handleScroll = () => {
    if (tabsRef.current) {
      const { scrollWidth, clientWidth, scrollLeft } = tabsRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft + clientWidth < scrollWidth);
    }
  };

  const scrollLeft = () => {
    if (tabsRef.current) {
      tabsRef.current.scrollBy({ left: -100, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (tabsRef.current) {
      tabsRef.current.scrollBy({ left: 100, behavior: 'smooth' });
    }
  };

  const handleTabChange = (value: string) => {
    setActiveTab(value);
    // Support both callback props for flexibility
    if (onChange) {
      onChange(value);
    }
    if (onValueChange) {
      onValueChange(value);
    }
  };

  return (
    <div className={cn("w-full", className)}>
      <div className="relative">
        {showScrollButtons && (
          <>
            <button
              onClick={scrollLeft}
              className={cn(
                "absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-background/80 backdrop-blur-sm p-1 rounded-full shadow-sm border", 
                "text-muted-foreground hover:text-foreground transition-all",
                !canScrollLeft && "opacity-0 pointer-events-none"
              )}
              aria-hidden={!canScrollLeft}
              tabIndex={canScrollLeft ? 0 : -1}
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              onClick={scrollRight}
              className={cn(
                "absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-background/80 backdrop-blur-sm p-1 rounded-full shadow-sm border",
                "text-muted-foreground hover:text-foreground transition-all",
                !canScrollRight && "opacity-0 pointer-events-none"
              )}
              aria-hidden={!canScrollRight}
              tabIndex={canScrollRight ? 0 : -1}
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </>
        )}

        <Tabs 
          defaultValue={activeTab} 
          value={activeTab}
          onValueChange={handleTabChange}
          className="w-full"
          orientation={orientation}
        >
          <div className={cn("relative", showScrollButtons && "mx-6")}>
            <div 
              ref={tabsRef}
              className="overflow-x-auto scrollbar-none" 
              onScroll={handleScroll}
            >
              <TabsList className={cn("w-full inline-flex whitespace-nowrap", tabsListClassName)}>
                {tabs.map((tab) => (
                  <TabsTrigger
                    key={tab.value}
                    value={tab.value}
                    disabled={tab.disabled}
                    className="flex-1 min-w-min whitespace-nowrap"
                  >
                    {tab.label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </div>
          </div>
          
          <div className={cn("mt-4", contentClassName)}>
            {tabs.map((tab) => (
              <TabsContent key={tab.value} value={tab.value}>
                {tab.content}
              </TabsContent>
            ))}
          </div>
        </Tabs>
      </div>
    </div>
  );
};

export default ResponsiveTabs;
